import styled from "@emotion/styled";
import { useEnsuredForwardedRef } from "@smartsheet/lodestar-utils";
import { callAllHandlers } from "@smartsheet/lodestar-utils";
import React, {
    ButtonHTMLAttributes,
    FocusEventHandler,
    forwardRef,
    MouseEventHandler,
    ReactNode,
    useContext,
} from "react";
import { Tab as BaseTab } from "reakit/Tab";
import { neutralDark30, neutralDark40, toRgba } from "../../constants/colors";
import { sizes } from "../../constants/spacing";
import { getFocusRingStyles } from "../../util/focusRing";
import { LodestarCoreTheme, ThemeMode } from "../../util/theme";
import { withFocusToggleClassName } from "../../util/withFocusClassName";
import { TabsContext } from "./TabsContext";

export const StyledTab = withFocusToggleClassName(styled(BaseTab, {
    shouldForwardProp: (prop) => prop !== "themeMode",
})<{
    selected: boolean;
    themeMode: ThemeMode;
}>`
    background-color: transparent;
    border: none;
    color: ${neutralDark30};
    font-size: 14px;
    font-weight: 600;
    // Override chrome's ua stylesheet which specifies buttons use Arial (wut?).
    font-family: inherit;
    line-height: 18px;
    margin: 0;
    padding: 0;
    height: 100%;
    cursor: pointer;

    // This is to ensure these tabs appear on top of the Underlines.
    position: relative;
    z-index: 2;

    :not(:first-of-type) {
        margin-left: ${sizes.xLarge}px;
    }

    ${({ selected }) => {
        if (selected) {
            return {
                color: neutralDark40,
            };
        }
    }}

    ${({ themeMode }) =>
        getFocusRingStyles({
            offset: 4,
            theme: themeMode,
            radius: 8,
            includeFocusPseudoClass: true,
        })}

    &:focus::before {
        // Customize the focusRingStyles from above.
        bottom: 0;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }

    &[aria-disabled="true"] {
        color: ${toRgba(neutralDark30, 0.4)};
    }
`);
StyledTab.displayName = "StyledTab";

export interface TabProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled"> {
    /**
     * The children to render inside this Tab.
     */
    children: ReactNode;
    /**
     * Flag to set the disabled state ot the tab.
     * @deprecated use isDisabled instead
     */
    disabled?: boolean;
    /**
     * If an id isn't provided, one will be automatically generated.
     * This is used to connect the Tab to its respective TabPanel.
     *
     * As always, this should be unique across the entire DOM at any given point in time.
     */
    id?: string;
    /**
     * Flag to set the disabled state ot the tab.
     */
    isDisabled?: boolean;
}

export const Tab = forwardRef<HTMLButtonElement, TabProps>(({ children, isDisabled, disabled, ...rest }, parentRef) => {
    const theme = useContext(LodestarCoreTheme);
    const { onlyRenderActiveTabPanelChildren: _0, onChange, ...tab } = React.useContext(TabsContext);
    const ref = useEnsuredForwardedRef(parentRef);
    const isSelected = ref.current?.id === tab.selectedId;

    /**
     * TODO: remove this in a future release with this ticket:
     * https://git.lab.smartsheet.com/team-ui-engineering/lodestar-core/-/issues/466
     */
    isDisabled = isDisabled || disabled;
    if (process.env.NODE_ENV !== "production") {
        if (disabled !== undefined) {
            console.warn("The 'disabled' prop has been deprecated in favor of 'isDisabled'. Please use that instead.");
        }
    }

    // Have to override these to preventDefault to block Reakit from doing its default thing which
    // is to select this tab.The onChange handler will propagate the change to the top and
    // then it will flow back here. If we don't prevent this default, the Tab will be focued by Reakit
    // then we'll call the onChange handler and propagate the change back to the top. By the time it reaches
    // the controlling code, Reakit's already focused the tab. We want to preventDefault here to force
    // Reakit into a controlled state.
    const onClick: MouseEventHandler<HTMLButtonElement> = callAllHandlers((e) => {
        e.preventDefault();
        if (!isSelected && !isDisabled && ref.current?.id) {
            onChange(ref.current.id);
        }
    }, rest.onClick);

    // Have to override these to preventDefault to block Reakit from doing its default thing which
    // is to select this tab.The onFocus handler will propagate the change to the top and
    // then it will flow back here. If we don't prevent this default, the Tab will be focused by Reakit
    // then we'll call the onChange handler and propagate the change back to the top. By the time it reaches
    // the controlling code, Reakit's already focused the tab. We want to preventDefault here to force
    // Reakit into a controlled state.
    const onFocus: FocusEventHandler<HTMLButtonElement> = callAllHandlers((e) => {
        e.preventDefault();
        if (!tab.manual && !isSelected && !isDisabled && ref.current?.id) {
            onChange(ref.current.id);
        }
    }, rest.onFocus);

    return (
        <StyledTab
            {...tab}
            {...rest}
            ref={ref}
            selected={isSelected}
            themeMode={theme}
            onClick={onClick}
            onFocus={onFocus}
            aria-disabled={isDisabled}
        >
            {children}
        </StyledTab>
    );
});

Tab.displayName = "Tab";
